import React, { FC } from 'react';
import { makeStyles } from '@material-ui/core/styles';

// Predefined color palette for physician assignments (36 colors)
const PHYSICIAN_COLOR_PALETTE = [
  '#3b82f6', // blue
  '#ef4444', // red
  '#10b981', // emerald
  '#f59e0b', // amber
  '#8b5cf6', // violet
  '#ec4899', // pink
  '#06b6d4', // cyan
  '#84cc16', // lime
  '#f97316', // orange
  '#6366f1', // indigo
  '#14b8a6', // teal
  '#a855f7', // purple
  '#22c55e', // green
  '#eab308', // yellow
  '#0ea5e9', // sky
  '#d946ef', // fuchsia
  '#64748b', // slate
  '#78716c', // stone
  '#f43f5e', // rose
  '#2dd4bf', // teal-light
  '#a3e635', // lime-light
  '#fbbf24', // amber-light
  '#38bdf8', // sky-light
  '#c084fc', // purple-light
  '#fb7185', // rose-light
  '#4ade80', // green-light
  '#facc15', // yellow-light
  '#818cf8', // indigo-light
  '#34d399', // emerald-light
  '#f472b6', // pink-light
  '#60a5fa', // blue-light
  '#fb923c', // orange-light
  '#a78bfa', // violet-light
  '#2563eb', // blue-dark
  '#dc2626', // red-dark
  '#059669', // emerald-dark
];

export interface Physician {
  id: string;
  name: string;
  color?: string;
}

export interface Procedure {
  patientName: string;
  physician: Physician;
  scheduledStartTime: string;
  actualStartTime: string;
  scheduledDuration: number;
  actualDuration: number;
  orEntry: string;
  orExit: string;
  procedureStart: string | null;
  closingStart: string | null;
  procedureEnd: string | null;
}

export interface ORMetrics {
  orName: string;
  fcots: number | null;
  fcotsOnTime: boolean;
  utilization: number;
  dailyAvailability: number | null;
  totalSurgeryTime: number;
  generalTurnoverAvg: number | null;
  surgeonTurnoverAvg: number | null;
  firstInTime: string | null;
  lastOutTime: string | null;
  firstInToLastOut: number | null;
  procedures: Procedure[];
}

export interface ORScheduleMetrics {
  date: string;
  totalProcedures: number;
  avgFcots: number | null;
  fcotsOnTimeRate: number | null;
  avgUtilization: number;
  totalSurgeryTimeHours: number;
  avgGeneralTurnover: number | null;
  avgSurgeonTurnover: number | null;
  lastPatientOutDelay: number | null;
  orMetrics: ORMetrics[];
}

export interface TooltipData {
  procedure: Procedure;
  position: { x: number; y: number };
}

// Creates a mapping of physician IDs to colors for a given set of metrics
// This is called once per render and memoized
export const createPhysicianColorMap = (orMetrics: ORMetrics[]): Map<string, string> => {
  const colorMap = new Map<string, string>();
  let colorIndex = 0;

  // Collect all unique physician IDs from all procedures
  for (const orMetric of orMetrics) {
    for (const procedure of orMetric.procedures) {
      const physicianId = procedure.physician?.id;
      if (physicianId && !colorMap.has(physicianId)) {
        colorMap.set(physicianId, PHYSICIAN_COLOR_PALETTE[colorIndex % PHYSICIAN_COLOR_PALETTE.length]);
        colorIndex++;
      }
    }
  }

  return colorMap;
};

// Configuration constants
const MARGIN_LEFT = 118;
const MARGIN_RIGHT = 290;
const MARGIN_TOP = 40;
const MARGIN_BOTTOM = 40;
const GROUP_HEIGHT = 70;
const BAR_HEIGHT = 20;
const SCHEDULED_LINE_HEIGHT = 4;
const CAPACITY_START_MINUTES = 7 * 60;

// Gap between chart area and right-side stats
const CHART_STATS_GAP = 28;

// Right-side stats column configuration (FCOTS is on the left, not here)
const STATS_COL_WIDTH = 60;
const STATS_COLS = [
  { key: 'utilization', label: 'Utilization' },
  { key: 'surgeryTime', label: 'Surgery\nTime' },
  { key: 'turnoverTime', label: 'Turnover\nTime' },
  { key: 'firstInLastOut', label: 'First-in\nLast-out' },
];

export const timeToMinutes = (timeStr: string): number | null => {
  if (!timeStr) return null;
  const match = timeStr.match(/(\d{1,2}):(\d{2})/);
  if (!match) return null;
  const hour = parseInt(match[1], 10);
  const minute = parseInt(match[2], 10);
  if (isNaN(hour) || isNaN(minute)) return null;
  return hour * 60 + minute;
};

const useStyles = makeStyles(() => ({
  svgRoot: {
    display: 'block',
  },
  interactiveGroup: {
    cursor: 'pointer',
  },
}));

const ProcedureBoundaryMarker: FC<{
  x: number;
  y: number;
  height: number;
  variant?: 'solid' | 'dotted';
  clampX?: { min: number; max: number };
}> = ({ x, y, height, variant = 'solid', clampX }) => {
  const stroke = 'rgba(255,255,255,0.85)';
  const strokeWidth = 1.5;
  const handleSize = 8;
  const handleGap = 2;
  const hasClamp = !!clampX && clampX.min <= clampX.max;
  const clampedRawX = hasClamp ? Math.max(clampX!.min, Math.min(x, clampX!.max)) : x;

  const snappedX = Math.round(clampedRawX) + 0.5;
  const alignedX = hasClamp ? Math.max(clampX!.min, Math.min(snappedX, clampX!.max)) : snappedX;
  const handleX = alignedX - handleSize / 2;
  const handleY = y - handleSize - handleGap;

  return (
    <g shapeRendering="crispEdges">
      {variant === 'dotted' ? (
        (() => {
          const dotSize = 2;
          const dotGap = 3;
          const inset = 1;
          const startY = y + inset;
          const endY = y + height - inset - dotSize;
          const dotX = alignedX - dotSize / 2;
          const dots: React.ReactNode[] = [];
          for (let yy = startY; yy <= endY; yy += dotSize + dotGap) {
            dots.push(<rect key={yy} x={dotX} y={yy} width={dotSize} height={dotSize} rx={1} fill={stroke} />);
          }
          return <g>{dots}</g>;
        })()
      ) : (
        <line x1={alignedX} y1={y} x2={alignedX} y2={y + height} stroke={stroke} strokeWidth={strokeWidth} />
      )}
      <polygon
        points={`${alignedX - 3},${y - handleGap - 4} ${alignedX + 3},${y - handleGap - 4} ${alignedX},${y - handleGap}`}
        fill="rgba(255,255,255,0.95)"
        stroke="rgba(15,23,42,0.65)"
        strokeWidth={1}
      />
    </g>
  );
};

const FCOTSBadge: FC<{ fcots: number; groupY: number }> = ({ fcots, groupY }) => {
  const isDelayed = fcots > 0;
  const fcotText = isDelayed ? `+${fcots}` : fcots === 0 ? '0' : String(fcots);
  const badgeColor = isDelayed ? '#ef4444' : '#10b981';
  const badgeBgColor = isDelayed ? 'rgba(239, 68, 68, 0.12)' : 'rgba(16, 185, 129, 0.12)';
  const badgeWidth = 50;
  const badgeHeight = 32;
  const badgeX = MARGIN_LEFT - badgeWidth - 15;
  const badgeY = groupY + 26;

  return (
    <g>
      <rect x={badgeX} y={badgeY} width={badgeWidth} height={badgeHeight} rx={6} fill={badgeBgColor} />
      <text
        x={badgeX + badgeWidth / 2}
        y={badgeY + 13}
        textAnchor="middle"
        fill={badgeColor}
        fontSize={13}
        fontWeight={700}
      >
        {fcotText}
      </text>
      <text
        x={badgeX + badgeWidth / 2}
        y={badgeY + 25}
        textAnchor="middle"
        fill={badgeColor}
        fontSize={8}
        fontWeight={600}
      >
        FCOTS
      </text>
    </g>
  );
};

interface GanttChartProps {
  metrics: ORScheduleMetrics;
  chartStartHour: number;
  chartEndHour: number;
  onProcedureHover: (data: TooltipData | null) => void;
  physicianColorMap: Map<string, string>;
}

const GanttChart: FC<GanttChartProps> = ({
  metrics,
  chartStartHour,
  chartEndHour,
  onProcedureHover,
  physicianColorMap,
}) => {
  const classes = useStyles();
  const numORs = metrics.orMetrics.length;
  const chartWidth = 1200;
  const chartHeight = MARGIN_TOP + numORs * GROUP_HEIGHT + MARGIN_BOTTOM;
  const chartAreaWidth = chartWidth - MARGIN_LEFT - MARGIN_RIGHT;
  const totalChartMinutes = (chartEndHour - chartStartHour) * 60;

  const minutesToX = (totalMinutes: number): number => {
    const chartStartMinutes = chartStartHour * 60;
    const minutesOffset = totalMinutes - chartStartMinutes;
    const clampedOffset = Math.max(0, Math.min(minutesOffset, totalChartMinutes));
    return MARGIN_LEFT + (clampedOffset / totalChartMinutes) * chartAreaWidth;
  };

  const hours = Array.from({ length: chartEndHour - chartStartHour + 1 }, (_, i) => chartStartHour + i);

  const handleMouseMove = (proc: Procedure) => (e: React.MouseEvent) => {
    onProcedureHover({
      procedure: proc,
      position: { x: e.clientX + 15, y: e.clientY - 15 },
    });
  };

  const handleMouseLeave = () => {
    onProcedureHover(null);
  };

  return (
    <svg
      className={classes.svgRoot}
      width="100%"
      viewBox={`0 0 ${chartWidth} ${chartHeight}`}
      preserveAspectRatio="xMinYMin meet"
    >
      {/* Grid lines - skip last hour to avoid double line with right edge/separator */}
      {hours.slice(0, -1).map(h => {
        const x = minutesToX(h * 60);
        return (
          <line
            key={`grid-${h}`}
            x1={x}
            y1={MARGIN_TOP}
            x2={x}
            y2={chartHeight - MARGIN_BOTTOM}
            stroke="rgba(156,163,175,0.25)"
            strokeWidth={1}
          />
        );
      })}

      {/* Top border line */}
      <line
        x1={MARGIN_LEFT}
        y1={MARGIN_TOP - 5}
        x2={chartWidth - MARGIN_RIGHT}
        y2={MARGIN_TOP - 5}
        stroke="#9ca3af"
        strokeWidth={2}
      />

      {/* Left border line */}
      <line
        x1={MARGIN_LEFT}
        y1={MARGIN_TOP}
        x2={MARGIN_LEFT}
        y2={chartHeight - MARGIN_BOTTOM}
        stroke="#9ca3af"
        strokeWidth={1}
      />

      {/* Bottom border line */}
      <line
        x1={MARGIN_LEFT}
        y1={chartHeight - MARGIN_BOTTOM}
        x2={chartWidth - MARGIN_RIGHT}
        y2={chartHeight - MARGIN_BOTTOM}
        stroke="#9ca3af"
        strokeWidth={2}
      />

      {/* Time labels - top */}
      {hours.map(h => {
        const x = minutesToX(h * 60);
        const timeStr = `${String(h).padStart(2, '0')}:00`;
        return (
          <text key={`time-top-${h}`} x={x} y={MARGIN_TOP - 15} textAnchor="middle" fill="#94a3b8" fontSize={14}>
            {timeStr}
          </text>
        );
      })}

      {/* Vertical separator between chart and stats (after gap) */}
      <line
        x1={chartWidth - MARGIN_RIGHT + CHART_STATS_GAP}
        y1={MARGIN_TOP - 5}
        x2={chartWidth - MARGIN_RIGHT + CHART_STATS_GAP}
        y2={chartHeight - MARGIN_BOTTOM}
        stroke="rgba(156, 163, 175, 0.2)"
        strokeWidth={1}
      />

      {/* Stats column headers */}
      {STATS_COLS.map((col, colIndex) => {
        const statsPanelLeft = chartWidth - MARGIN_RIGHT + CHART_STATS_GAP + 12;
        const colX = statsPanelLeft + colIndex * STATS_COL_WIDTH + STATS_COL_WIDTH / 2;
        const lines = col.label.split('\n');
        return (
          <g key={`header-${col.key}`}>
            {lines.map((line, lineIndex) => (
              <text
                key={`header-${col.key}-${lineIndex}`}
                x={colX}
                y={MARGIN_TOP - 20 + lineIndex * 12}
                textAnchor="middle"
                fill="#64748b"
                fontSize={10}
                fontWeight={600}
              >
                {line}
              </text>
            ))}
          </g>
        );
      })}

      {/* Time labels - bottom */}
      {hours.map(h => {
        const x = minutesToX(h * 60);
        const timeStr = `${String(h).padStart(2, '0')}:00`;
        return (
          <text key={`time-bottom-${h}`} x={x} y={chartHeight - 10} textAnchor="middle" fill="#94a3b8" fontSize={14}>
            {timeStr}
          </text>
        );
      })}

      {/* OR Rows */}
      {metrics.orMetrics.map((orMetric, index) => {
        const groupY = MARGIN_TOP + index * GROUP_HEIGHT;
        const actualY = groupY + 20;
        const scheduledY = groupY + 50;

        return (
          <g key={`or-${index}`}>
            {/* Alternating row background */}
            {index % 2 === 0 && (
              <rect
                x={MARGIN_LEFT}
                y={groupY}
                width={chartWidth - MARGIN_LEFT - MARGIN_RIGHT}
                height={GROUP_HEIGHT}
                fill="rgba(255,255,255,0.02)"
              />
            )}

            {/* Capacity line */}
            {orMetric.dailyAvailability !== null && orMetric.dailyAvailability > 0 && (
              <line
                x1={minutesToX(CAPACITY_START_MINUTES)}
                y1={actualY + BAR_HEIGHT / 2}
                x2={minutesToX(CAPACITY_START_MINUTES + orMetric.dailyAvailability)}
                y2={actualY + BAR_HEIGHT / 2}
                stroke="#059669"
                strokeWidth={1}
                strokeDasharray="3,3"
              />
            )}

            {/* OR Label */}
            <text x={MARGIN_LEFT - 10} y={groupY + 15} textAnchor="end" fill="#ffffff" fontSize={14} fontWeight={700}>
              {orMetric.orName}
            </text>

            {/* FCOTS badge - left side */}
            {orMetric.fcots !== null && <FCOTSBadge fcots={orMetric.fcots} groupY={groupY} />}

            {/* Right-side stats columns (Utilization, Surgery Time, Turnover Time, First-in Last-out) */}
            {(() => {
              const statsPanelLeft = chartWidth - MARGIN_RIGHT + CHART_STATS_GAP + 12;
              const centerY = groupY + GROUP_HEIGHT / 2;

              const statsData = [
                {
                  key: 'utilization',
                  render: (x: number) => (
                    <text x={x} y={centerY + 4} textAnchor="middle" fill="#e5e7eb" fontSize={11}>
                      {orMetric.utilization !== null ? `${orMetric.utilization.toFixed(1)}%` : '—'}
                    </text>
                  ),
                },
                {
                  key: 'surgeryTime',
                  render: (x: number) => (
                    <text x={x} y={centerY + 4} textAnchor="middle" fill="#e5e7eb" fontSize={11}>
                      {orMetric.totalSurgeryTime ? `${Math.round(orMetric.totalSurgeryTime)} min` : '—'}
                    </text>
                  ),
                },
                {
                  key: 'turnoverTime',
                  render: (x: number) => (
                    <text x={x} y={centerY + 4} textAnchor="middle" fill="#e5e7eb" fontSize={11}>
                      {orMetric.generalTurnoverAvg !== null ? `${Math.round(orMetric.generalTurnoverAvg)} min` : '—'}
                    </text>
                  ),
                },
                {
                  key: 'firstInLastOut',
                  render: (x: number) => (
                    <text x={x} y={centerY + 4} textAnchor="middle" fill="#e5e7eb" fontSize={11}>
                      {orMetric.firstInToLastOut !== null ? `${Math.round(orMetric.firstInToLastOut)} min` : '—'}
                    </text>
                  ),
                },
              ];

              return statsData.map((stat, colIndex) => {
                const colX = statsPanelLeft + colIndex * STATS_COL_WIDTH + STATS_COL_WIDTH / 2;
                return <g key={stat.key}>{stat.render(colX)}</g>;
              });
            })()}

            {/* Procedures */}
            {orMetric.procedures.map((proc, procIndex) => {
              const physicianId = proc.physician?.id;
              const physicianColor = (physicianId && physicianColorMap.get(physicianId)) || '#6b7280';
              const prevProc = procIndex > 0 ? orMetric.procedures[procIndex - 1] : null;

              const actualStartMin = proc.actualStartTime ? timeToMinutes(proc.actualStartTime) : null;
              const scheduledStartMin = proc.scheduledStartTime ? timeToMinutes(proc.scheduledStartTime) : null;

              // Calculate turnover
              let turnoverDuration: number | null = null;
              if (prevProc && actualStartMin !== null) {
                const prevStartMin = prevProc.actualStartTime ? timeToMinutes(prevProc.actualStartTime) : null;
                if (prevStartMin !== null && prevProc.actualDuration) {
                  const prevEndMin = prevStartMin + prevProc.actualDuration;
                  turnoverDuration = actualStartMin - prevEndMin;
                }
              }

              return (
                <g key={`proc-${procIndex}`}>
                  {/* Actual bar */}
                  {actualStartMin !== null && proc.actualDuration && proc.actualDuration > 0 && (
                    <g
                      className={classes.interactiveGroup}
                      onMouseMove={handleMouseMove(proc)}
                      onMouseLeave={handleMouseLeave}
                    >
                      <rect
                        x={minutesToX(actualStartMin)}
                        y={actualY}
                        width={Math.max(
                          0,
                          minutesToX(actualStartMin + proc.actualDuration) - minutesToX(actualStartMin)
                        )}
                        height={BAR_HEIGHT}
                        fill={physicianColor}
                      />
                      {/* Procedure Start marker */}
                      {proc.procedureStart &&
                        (() => {
                          const procStartMin = timeToMinutes(proc.procedureStart);
                          if (procStartMin === null) return null;
                          const markerX = minutesToX(procStartMin);
                          const barStartX = minutesToX(actualStartMin);
                          const barEndX = minutesToX(actualStartMin + proc.actualDuration);
                          // Only show if marker is within the bar bounds
                          if (markerX < barStartX || markerX > barEndX) return null;
                          return (
                            <ProcedureBoundaryMarker
                              x={markerX}
                              y={actualY}
                              height={BAR_HEIGHT}
                              variant="dotted"
                              clampX={{ min: barStartX + 0.5, max: barEndX - 0.5 }}
                            />
                          );
                        })()}
                      {/* Procedure End marker */}
                      {proc.procedureEnd &&
                        (() => {
                          const procEndMin = timeToMinutes(proc.procedureEnd);
                          if (procEndMin === null) return null;
                          const markerX = minutesToX(procEndMin);
                          const barStartX = minutesToX(actualStartMin);
                          const barEndX = minutesToX(actualStartMin + proc.actualDuration);
                          // Only show if marker is within the bar bounds
                          if (markerX < barStartX || markerX > barEndX) return null;
                          return (
                            <ProcedureBoundaryMarker
                              x={markerX}
                              y={actualY}
                              height={BAR_HEIGHT}
                              clampX={{ min: barStartX + 0.5, max: barEndX - 0.5 }}
                            />
                          );
                        })()}
                      {/* Duration text */}
                      {minutesToX(actualStartMin + proc.actualDuration) - minutesToX(actualStartMin) > 30 && (
                        <text
                          x={
                            minutesToX(actualStartMin) +
                            (minutesToX(actualStartMin + proc.actualDuration) - minutesToX(actualStartMin)) / 2
                          }
                          y={actualY + BAR_HEIGHT / 2 + 4}
                          textAnchor="middle"
                          fill="white"
                          fontSize={10}
                        >
                          {Math.round(proc.actualDuration)}
                        </text>
                      )}
                    </g>
                  )}

                  {/* Scheduled line */}
                  {scheduledStartMin !== null &&
                    proc.scheduledDuration &&
                    proc.scheduledDuration > 0 &&
                    (() => {
                      const scheduledStartX = minutesToX(scheduledStartMin);
                      const scheduledEndX = minutesToX(scheduledStartMin + proc.scheduledDuration);
                      const scheduledWidth = Math.max(0, scheduledEndX - scheduledStartX);
                      const labelCenterX = (scheduledStartX + scheduledEndX) / 2;
                      const labelWidth = 30;
                      const labelHeight = 14;
                      const labelX = labelCenterX - labelWidth / 2;
                      const labelY = scheduledY + 6;

                      return (
                        <g
                          className={classes.interactiveGroup}
                          onMouseMove={handleMouseMove(proc)}
                          onMouseLeave={handleMouseLeave}
                        >
                          <rect
                            x={scheduledStartX}
                            y={scheduledY}
                            width={scheduledWidth}
                            height={SCHEDULED_LINE_HEIGHT}
                            fill={physicianColor}
                            opacity={0.6}
                          />
                          {/* End marker */}
                          <rect
                            x={scheduledEndX - 1}
                            y={scheduledY - 2}
                            width={2}
                            height={SCHEDULED_LINE_HEIGHT + 4}
                            fill={physicianColor}
                          />
                          {/* Scheduled duration pill */}
                          {scheduledWidth > 40 && (
                            <g>
                              <rect
                                x={labelX}
                                y={labelY}
                                width={labelWidth}
                                height={labelHeight}
                                rx={7}
                                fill="rgba(15,23,42,0.85)"
                                stroke={physicianColor}
                                strokeWidth={0.5}
                              />
                              <text
                                x={labelCenterX}
                                y={labelY + 10}
                                textAnchor="middle"
                                fill="#e5e7eb"
                                fontSize={9}
                                fontWeight={500}
                              >
                                {Math.round(proc.scheduledDuration)}
                              </text>
                            </g>
                          )}
                        </g>
                      );
                    })()}

                  {/* Turnover time label */}
                  {turnoverDuration !== null &&
                    turnoverDuration > 0 &&
                    prevProc &&
                    (() => {
                      const prevStartMin = prevProc.actualStartTime ? timeToMinutes(prevProc.actualStartTime) : null;
                      if (prevStartMin === null || !prevProc.actualDuration || actualStartMin === null) return null;
                      const prevEndX = minutesToX(prevStartMin + prevProc.actualDuration);
                      const currentStartX = minutesToX(actualStartMin);
                      return (
                        <text
                          x={(prevEndX + currentStartX) / 2}
                          y={actualY + BAR_HEIGHT / 2 + 4}
                          textAnchor="middle"
                          fill="#facc15"
                          fontSize={11}
                          fontWeight={700}
                        >
                          {Math.round(turnoverDuration)}
                        </text>
                      );
                    })()}
                </g>
              );
            })}

            {/* Row separator - chart area */}
            <line
              x1={MARGIN_LEFT}
              y1={groupY + GROUP_HEIGHT}
              x2={chartWidth - MARGIN_RIGHT}
              y2={groupY + GROUP_HEIGHT}
              stroke="rgba(229, 231, 235, 0.15)"
              strokeWidth={1}
            />
            {/* Row separator - stats area (after the gap) */}
            <line
              x1={chartWidth - MARGIN_RIGHT + CHART_STATS_GAP}
              y1={groupY + GROUP_HEIGHT}
              x2={chartWidth - 10}
              y2={groupY + GROUP_HEIGHT}
              stroke="rgba(229, 231, 235, 0.15)"
              strokeWidth={1}
            />
          </g>
        );
      })}
    </svg>
  );
};

export default GanttChart;
